import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MailOpen, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterSignup = () => {
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null); // "new" | "already"

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const { data } = await axios.post(`${API}/newsletter/subscribe`, { email });
            setResult(data.already ? "already" : "new");
            toast.success(
                data.already
                    ? "Este correo ya estaba apuntado."
                    : "Gracias. Te he escrito una carta de bienvenida."
            );
        } catch {
            toast.error("No se pudo apuntar tu correo. Inténtalo de nuevo en un momento.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="newsletter" className="border-b border-line">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:items-end">
                <div className="md:col-span-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-crimson">
                        Cartas al correo
                    </p>
                    <p className="mt-3 font-display text-3xl text-ink">Recibe cada poema nuevo</p>
                    <p className="mt-3 max-w-md text-base leading-relaxed text-ink/70">
                        Apunta tu correo y te avisaré cuando publique un poema, una canción o un
                        vídeo poema nuevo. Sin ruido: solo versos.
                    </p>
                </div>
                <div className="md:col-span-6">
                    {result ? (
                        <motion.p
                            data-testid="newsletter-success"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-start gap-3 font-display italic text-xl text-ink/85"
                        >
                            <MailOpen size={20} className="mt-1 shrink-0 text-crimson" />
                            {result === "already"
                                ? "Este correo ya estaba en la lista. Te escribiré cuando haya algo nuevo."
                                : "Gracias por apuntarte. Revisa tu correo: te he escrito una carta de bienvenida."}
                        </motion.p>
                    ) : (
                        <form
                            data-testid="newsletter-form"
                            onSubmit={submit}
                            className="flex flex-col sm:flex-row sm:items-end gap-5"
                        >
                            <input
                                data-testid="newsletter-email-input"
                                required
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@correo.com"
                                aria-label="Tu correo electrónico"
                                className="flex-1 bg-transparent border-b border-ink/50 focus:border-crimson outline-none py-3 text-lg text-ink placeholder:text-subtle transition-colors"
                            />
                            <button
                                data-testid="newsletter-submit-button"
                                type="submit"
                                disabled={sending}
                                className="group self-start sm:self-auto flex items-center gap-3 bg-ink text-paper px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-crimson disabled:opacity-50 transition-colors duration-300"
                            >
                                {sending ? "Apuntando…" : "Quiero recibirlos"}
                                <Send size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                        </form>
                    )}
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                        Nada de publicidad — solo poesía, cuando haya algo nuevo que contar
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NewsletterSignup;
